import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarRange } from "lucide-react";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import { campaignTimeline, TIPO_META, campInicio, campFim, campTipos, type CampanhaTipo } from "../data/campaignTimeline";

const MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const PX_DIA = 4.6;
const LABEL_W = 250;
const LANE_H = 24;

function parse(d: string): Date {
  const p = d.split("-");
  return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
}
function dias(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}
function fmt(d: string): string {
  const dt = parse(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}`;
}

const FILTROS: Array<CampanhaTipo | "todos"> = ["todos", "captacao", "vendas", "sazonal", "sempre-ligado", "institucional"];

export default function Timeline() {
  const [filtro, setFiltro] = useState<CampanhaTipo | "todos">("todos");

  const escala = useMemo(() => {
    const relevantes = campaignTimeline.filter((c) => c.fases.some((f) => f.tipo !== "sempre-ligado")).map((c) => parse(campInicio(c)));
    const startsBase = relevantes.length ? relevantes : campaignTimeline.map((c) => parse(campInicio(c)));
    const ends = campaignTimeline.map((c) => parse(campFim(c)));
    const min = new Date(Math.min(...startsBase.map((d) => d.getTime())));
    const max = new Date(Math.max(...ends.map((d) => d.getTime())));
    const rs = new Date(min.getFullYear(), min.getMonth(), 1);
    const re = new Date(max.getFullYear(), max.getMonth() + 1, 0);
    const totalDays = dias(rs, re) + 1;
    const months: Array<{ label: string; off: number; days: number }> = [];
    let cur = new Date(rs);
    while (cur <= re) {
      const dim = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
      months.push({ label: `${MES[cur.getMonth()]} ${String(cur.getFullYear()).slice(2)}`, off: dias(rs, cur), days: dim });
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return { rangeStart: rs, rangeEnd: re, months, totalDays, hojeOff: dias(rs, hoje) };
  }, []);

  const itens = useMemo(() => {
    const base = filtro === "todos" ? campaignTimeline : campaignTimeline.filter((c) => campTipos(c).includes(filtro));
    return [...base].sort((a, b) => campInicio(a).localeCompare(campInicio(b)) || campFim(a).localeCompare(campFim(b)));
  }, [filtro]);

  const larguraTotal = escala.totalDays * PX_DIA;

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Timeline de Campanhas"
        subtitle="Linha do tempo do plano de mídia — cada campanha com suas fases (captação, venda). Clique para ver o detalhe e o histórico de alterações."
        icon={<CalendarRange size={22} strokeWidth={2} />}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTROS.map((t) => {
          const ativo = filtro === t;
          return (
            <button
              key={t}
              onClick={() => setFiltro(t)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${ativo ? "bg-esm-brown-900 text-white border-esm-brown-900" : "bg-white text-esm-gray-600 border-esm-gray-200 hover:border-esm-gray-300"}`}
            >
              {t !== "todos" && <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: TIPO_META[t].cor }} />}
              {t === "todos" ? "Todos" : TIPO_META[t].label}
            </button>
          );
        })}
      </div>

      <Card padded={false} className="overflow-hidden">
        <div className="overflow-x-auto">
          <div style={{ minWidth: LABEL_W + larguraTotal }}>
            {/* Cabeçalho de meses */}
            <div className="flex border-b border-esm-gray-100 bg-esm-cream-50">
              <div style={{ width: LABEL_W }} className="flex-shrink-0 px-4 py-2 text-[11px] font-bold text-esm-gray-500 uppercase tracking-wide">
                Campanha
              </div>
              <div className="relative flex-shrink-0" style={{ width: larguraTotal, height: 34 }}>
                {escala.months.map((m, i) => (
                  <div
                    key={i}
                    className="absolute top-0 h-full flex items-center border-l border-esm-gray-100 pl-2 text-[11px] font-semibold text-esm-gray-500 capitalize"
                    style={{ left: m.off * PX_DIA, width: m.days * PX_DIA }}
                  >
                    {m.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Linhas */}
            <div className="relative">
              <div className="absolute inset-y-0 pointer-events-none" style={{ left: LABEL_W, width: larguraTotal }}>
                {escala.months.map((m, i) => (
                  <div key={i} className="absolute inset-y-0 border-l border-esm-gray-100" style={{ left: m.off * PX_DIA }} />
                ))}
                {escala.hojeOff >= 0 && escala.hojeOff <= escala.totalDays && (
                  <div className="absolute inset-y-0 border-l-2 border-esm-error z-10" style={{ left: escala.hojeOff * PX_DIA }}>
                    <span className="absolute top-0.5 left-1 text-[9px] font-bold text-esm-error bg-white/90 px-1 rounded">hoje</span>
                  </div>
                )}
              </div>

              {itens.map((c) => {
                const trackH = c.fases.length * LANE_H + 8;
                const opac = c.status === "concluido" ? 0.5 : c.status === "planejado" ? 0.8 : 1;
                return (
                  <Link key={c.id} to={`/timeline/${c.id}`} className="flex items-stretch border-b border-esm-gray-50 hover:bg-esm-cream-50/50 cursor-pointer">
                    <div style={{ width: LABEL_W }} className="flex-shrink-0 px-4 py-2.5">
                      <div className="text-[13px] font-semibold text-esm-gray-900 leading-tight">{c.nome}</div>
                      <div className="text-[10.5px] text-esm-gray-400 mt-0.5 flex flex-wrap items-center gap-x-2">
                        {campTipos(c).map((t) => (
                          <span key={t}>
                            <span style={{ color: TIPO_META[t].cor }}>●</span> {TIPO_META[t].label}
                          </span>
                        ))}
                        <span className="text-esm-gray-300">· {c.plataforma}</span>
                      </div>
                    </div>
                    <div className="relative flex-shrink-0" style={{ width: larguraTotal, height: trackH }}>
                      {c.fases.map((f, fi) => {
                        const s = parse(f.inicio) < escala.rangeStart ? escala.rangeStart : parse(f.inicio);
                        const e = parse(f.fim) > escala.rangeEnd ? escala.rangeEnd : parse(f.fim);
                        const off = dias(escala.rangeStart, s) * PX_DIA;
                        const w = Math.max((dias(s, e) + 1) * PX_DIA, 8);
                        const fm = TIPO_META[f.tipo];
                        return (
                          <div
                            key={fi}
                            className="absolute rounded-md flex items-center px-2 shadow-sm"
                            style={{ left: off, width: w, top: 4 + fi * LANE_H, height: LANE_H - 6, background: fm.cor, opacity: opac }}
                            title={`${fm.label}: ${fmt(f.inicio)}–${fmt(f.fim)}`}
                          >
                            <span className="text-[9.5px] font-semibold text-white whitespace-nowrap overflow-hidden">{fmt(f.inicio)}–{fmt(f.fim)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      <p className="text-[11px] text-esm-gray-400 mt-3 leading-relaxed">
        {itens.length} campanhas · fonte: plano de mídia (Obsidian). Fases empilhadas na mesma linha (● captação, ● venda). Barra sólida = ativo, clara = planejado, esmaecida = concluído. A linha vermelha é hoje. Editar em{" "}
        <code className="text-esm-gray-500">apps/dashboard/src/data/campaignTimeline.ts</code>.
      </p>
    </div>
  );
}
